import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { LimitedColorAssigner } from '@/modules/finances/lib/constants';
import { existingColors, formatBudgetName, getCorrectMetricValuesOverViewChart, hasSubLevels, percentageRespectTo, removeBudgetWord, removePatternAfterSlash, transformPathToName } from '@/modules/finances/lib/utils';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import type { AnalyticMetric, BreakdownBudgetAnalytic, Budget, BudgetMetricWithName, DoughnutSeries } from '../../types';
export const useCardChartOverview = (budgets, budgetsAnalytics, levelNumber, allBudgets, codePath) => {
  const $ = _c(34);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      from: "sm",
      to: "lg"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isTable = useMediaQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      from: "lg",
      to: "xl"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDesk1024 = useMediaQuery(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      from: "xl",
      to: "2xl"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isDesk1280 = useMediaQuery(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "2xl"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isUpDesktop1536 = useMediaQuery(t3);
  const isHasSubLevels = hasSubLevels(codePath, allBudgets);
  const [selectedMetric, setSelectedMetric] = useState("Budget");
  let t4;
  if ($[4] !== budgetsAnalytics) {
    bb0: {
      const data = {
        budget: 0,
        paymentsOnChain: 0,
        forecast: 0,
        actuals: 0
      };
      if (!budgetsAnalytics) {
        t4 = data;
        break bb0;
      }
      const values = Object.values(budgetsAnalytics);
      values.forEach(item => {
        item.forEach(element => {
          data.budget = data.budget + element.budget.value;
          data.forecast = data.forecast + element.forecast.value;
          data.actuals = data.actuals + element.actuals.value;
          data.paymentsOnChain = data.paymentsOnChain + element.paymentsOnChain.value;
        });
      });
      t4 = data;
    }
    $[4] = budgetsAnalytics;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const budgetWithNotChildren = t4;
  let t5;
  if ($[6] !== allBudgets || $[7] !== budgets || $[8] !== budgetsAnalytics || $[9] !== codePath) {
    const metric = {
      actuals: 0,
      forecast: 0,
      budget: 0,
      paymentsOnChain: 0,
      protocolNetOutflow: 0,
      paymentsOffChainIncluded: 0
    };
    const budgetMetrics = {};
    budgets.forEach(budget => {
      const budgetKey = budget.codePath;
      const budgetName = budget.name ? formatBudgetName(budget.name) : transformPathToName(budget.codePath);
      const budgetCode = budget.code ? budget.code : transformPathToName(budget.codePath);
      if (budget.codePath in budgetMetrics) {
        const uniqueKey = `${budgetKey}-${budget.id}`;
        budgetMetrics[uniqueKey] = {
          name: budgetName,
          code: budgetCode,
          actuals: {
            unit: "DAI",
            value: 0
          },
          forecast: {
            unit: "DAI",
            value: 0
          },
          budget: {
            unit: "DAI",
            value: 0
          },
          paymentsOnChain: {
            unit: "DAI",
            value: 0
          },
          paymentsOffChainIncluded: {
            unit: "DAI",
            value: 0
          },
          protocolNetOutflow: {
            unit: "DAI",
            value: 0
          }
        };
      } else {
        budgetMetrics[budgetKey] = {
          name: budgetName,
          code: budgetCode,
          actuals: {
            unit: "DAI",
            value: 0
          },
          forecast: {
            unit: "DAI",
            value: 0
          },
          budget: {
            unit: "DAI",
            value: 0
          },
          paymentsOnChain: {
            unit: "DAI",
            value: 0
          },
          paymentsOffChainIncluded: {
            unit: "DAI",
            value: 0
          },
          protocolNetOutflow: {
            unit: "DAI",
            value: 0
          }
        };
      }
    });
    if (budgetsAnalytics !== undefined) {
      const currentLevelBudgetPaths = new Set(budgets.map(_temp));
      for (const budgetMetricKey of Object.keys(budgetsAnalytics)) {
        const normalizedBudgetMetricKey = removePatternAfterSlash(budgetMetricKey);
        if (!currentLevelBudgetPaths.has(normalizedBudgetMetricKey)) {
          continue;
        }
        const budgetMetric = budgetsAnalytics[budgetMetricKey];
        const searchCorrectBudget = budgets.length > 0 ? budgets : allBudgets;
        const correspondingBudget = searchCorrectBudget.find(budget_1 => budget_1.codePath === normalizedBudgetMetricKey);
        const budgetName_0 = correspondingBudget ? formatBudgetName(correspondingBudget.name) : transformPathToName(budgetMetricKey);
        const budgetCode_0 = correspondingBudget?.code ?? transformPathToName(budgetMetricKey);
        metric.actuals = metric.actuals + (budgetMetric[0].actuals.value || 0);
        metric.forecast = metric.forecast + (budgetMetric[0].forecast.value || 0);
        metric.budget = metric.budget + (budgetMetric[0].budget.value || 0);
        metric.paymentsOnChain = metric.paymentsOnChain + Math.abs(budgetMetric[0].paymentsOnChain.value || 0);
        metric.protocolNetOutflow = metric.protocolNetOutflow + (budgetMetric[0].protocolNetOutflow.value || 0);
        budgetMetrics[normalizedBudgetMetricKey] = {
          name: budgetName_0,
          actuals: budgetMetric[0].actuals,
          forecast: budgetMetric[0].forecast,
          budget: budgetMetric[0].budget,
          paymentsOnChain: budgetMetric[0].paymentsOnChain,
          paymentsOffChainIncluded: budgetMetric[0].paymentsOffChainIncluded,
          protocolNetOutflow: budgetMetric[0].protocolNetOutflow,
          code: budgetCode_0
        };
      }
    }
    if (Object.keys(budgetMetrics).length === 0) {
      let t6;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          unit: "DAI",
          value: 0
        };
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      const emptyValue = t6;
      let t7;
      if ($[12] !== codePath) {
        t7 = transformPathToName(codePath);
        $[12] = codePath;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      let t8;
      if ($[14] !== codePath) {
        t8 = transformPathToName(codePath);
        $[14] = codePath;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      let t9;
      if ($[16] !== t7 || $[17] !== t8) {
        t9 = {
          name: t7,
          actuals: emptyValue,
          forecast: emptyValue,
          budget: emptyValue,
          paymentsOnChain: emptyValue,
          paymentsOffChainIncluded: emptyValue,
          protocolNetOutflow: emptyValue,
          code: t8
        };
        $[16] = t7;
        $[17] = t8;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      budgetMetrics[codePath] = t9;
    }
    t5 = {
      metric,
      budgetMetrics
    };
    $[6] = allBudgets;
    $[7] = budgets;
    $[8] = budgetsAnalytics;
    $[9] = codePath;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    metric: metric_0,
    budgetMetrics: budgetMetrics_0
  } = t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = metric_1 => {
      setSelectedMetric(metric_1);
    };
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleSelectedMetric = t6;
  let t7;
  if ($[20] !== budgetMetrics_0 || $[21] !== metric_0 || $[22] !== selectedMetric) {
    let t8;
    if ($[24] !== budgetMetrics_0) {
      t8 = Object.keys(budgetMetrics_0);
      $[24] = budgetMetrics_0;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    const colorAssigner = new LimitedColorAssigner(t8.length, existingColors);
    const keys = Object.keys(budgetMetrics_0);
    t7 = keys.sort().map(item_0 => {
      let value;
      bb76: switch (selectedMetric) {
        case "Actuals":
          {
            value = budgetMetrics_0[item_0].actuals.value || 0;
            break bb76;
          }
        case "Forecast":
          {
            value = budgetMetrics_0[item_0].forecast.value || 0;
            break bb76;
          }
        case "PaymentsOnChain":
          {
            value = budgetMetrics_0[item_0].paymentsOnChain.value || 0;
            break bb76;
          }
        case "ProtocolNetOutflow":
          {
            value = budgetMetrics_0[item_0].protocolNetOutflow.value || 0;
            break bb76;
          }
        case "Budget":
          {
            value = budgetMetrics_0[item_0].budget.value || 0;
            break bb76;
          }
        default:
          {
            value = budgetMetrics_0[item_0].budget.value || 0;
          }
      }
      const keyMetricValue = getCorrectMetricValuesOverViewChart(selectedMetric);
      const color = keys.length === 1 && value === 0 ? "rgb(204, 204, 204)" : colorAssigner.getColor(item_0);
      return {
        name: removeBudgetWord(budgetMetrics_0[item_0].name),
        code: budgetMetrics_0[item_0].code,
        value,
        originalValue: value,
        metrics: budgetMetrics_0[item_0],
        percent: percentageRespectTo(Math.abs(value), metric_0[keyMetricValue]),
        color,
        isVisible: true,
        originalColor: color
      };
    });
    $[20] = budgetMetrics_0;
    $[21] = metric_0;
    $[22] = selectedMetric;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const doughnutSeriesData = t7;
  const numberItems = doughnutSeriesData.length;
  const changeAlignment = numberItems > 4;
  const showSwiper = (isTable || isDesk1024) && numberItems >= 4 || (isDesk1280 || isUpDesktop1536) && numberItems >= 10;
  const isDeepLevel = doughnutSeriesData.length > 6;
  let t8;
  bb1: {
    if (isTable || isDesk1024) {
      t8 = isDeepLevel ? 5 : 3;
      break bb1;
    }
    if (isDesk1280 || isUpDesktop1536) {
      t8 = numberItems >= 10 ? 12 : 5;
      break bb1;
    }
    t8 = 5;
  }
  const numberSliderPerLevel = t8;
  const t9 = isHasSubLevels ? metric_0.paymentsOnChain : budgetWithNotChildren.paymentsOnChain;
  const t10 = isHasSubLevels ? metric_0.budget : budgetWithNotChildren.budget;
  let t11;
  if ($[26] !== changeAlignment || $[27] !== doughnutSeriesData || $[28] !== numberSliderPerLevel || $[29] !== selectedMetric || $[30] !== showSwiper || $[31] !== t10 || $[32] !== t9) {
    t11 = {
      paymentsOnChain: t9,
      budgetCap: t10,
      selectedMetric,
      handleSelectedMetric,
      doughnutSeriesData,
      changeAlignment,
      showSwiper,
      numberSliderPerLevel
    };
    $[26] = changeAlignment;
    $[27] = doughnutSeriesData;
    $[28] = numberSliderPerLevel;
    $[29] = selectedMetric;
    $[30] = showSwiper;
    $[31] = t10;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
};
function _temp(budget_0) {
  return budget_0.codePath;
}